/**
 * Statutory registers (P4d) — bye-laws 141–142 put these in the Secretary's
 * custody; Bero generates them LIVE from data the app already holds, so
 * they're permanently inspection-ready (Sec. 160). Three registers:
 * Members (I/J) · Minutes Book · Cash Book (running balance).
 * Committee-tier read (governance.compliance R — Secretary/Chairman/MC/Expert).
 * Wired: GET /api/registers{,/members,/minutes,/cash}.
 */
import React from 'react';
import { View } from 'react-native';

import { formatINR, space } from '@bero/tokens';
import { goBack } from '@/features/account-admin/bits';
import { Icon } from '@/features/account-admin/icons';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import {
  AppText, Card, Chip, EmptyState, Hairline, Pill, Screen, Skeleton, TopBar,
} from '@/ui';

type RegisterMeta = { key: string; title: string; sub: string; citation: string };
type MemberRow = { sr: number; name: string; memberClass: string; flat: string | null; role: string; admittedOn: string };
type MinutesRow = {
  date: string | null; kind: string; title: string; venue: string;
  present: number | null; quorumRequired: number | null; quorumMet: boolean | null;
  minutes: string; recordedBy: string | null; confirmedBy: string | null;
};
type CashRow = { date: string | null; particulars: string; reference: string | null; in: number; out: number; balance: number };

function fmtDay(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function RegistersScreen() {
  const { colors } = useTheme();
  const [metas, setMetas] = React.useState<RegisterMeta[]>([]);
  const [active, setActive] = React.useState('members');
  const [members, setMembers] = React.useState<MemberRow[] | null>(null);
  const [minutes, setMinutes] = React.useState<MinutesRow[] | null>(null);
  const [cash, setCash] = React.useState<{ items: CashRow[]; closingBalance: number } | null>(null);
  const [state, setState] = React.useState<'loading' | 'ready' | 'error' | 'forbidden'>('loading');

  const load = React.useCallback(async () => {
    setState('loading');
    try {
      const [meta, m, mb, cb] = await Promise.all([
        api.get<{ items: RegisterMeta[] }>('/api/registers'),
        api.get<{ items: MemberRow[] }>('/api/registers/members'),
        api.get<{ items: MinutesRow[] }>('/api/registers/minutes'),
        api.get<{ items: CashRow[]; closingBalance: number }>('/api/registers/cash'),
      ]);
      setMetas(meta.items ?? []);
      setMembers(m.items ?? []);
      setMinutes(mb.items ?? []);
      setCash({ items: cb.items ?? [], closingBalance: cb.closingBalance ?? 0 });
      setState('ready');
    } catch (e) {
      setState((e as { status?: number })?.status === 403 ? 'forbidden' : 'error');
    }
  }, []);
  React.useEffect(() => { void Promise.resolve().then(() => load()); }, [load]);

  const meta = metas.find((x) => x.key === active);

  return (
    <Screen scroll={false}>
      <TopBar title="Registers" onBack={goBack} />
      <Screen padded style={{ paddingTop: space.s3 }}>
        {state === 'loading' ? (
          <View style={{ gap: space.s3 }}>{[0, 1, 2].map((i) => <Skeleton key={i} h={88} />)}</View>
        ) : state === 'forbidden' ? (
          <EmptyState
            icon={<Icon name="shield" size={40} color={colors.textTertiary} />}
            title="Committee only"
            body="The statutory registers are kept for the committee and office bearers."
          />
        ) : state === 'error' ? (
          <EmptyState
            icon={<Icon name="alert-circle" size={40} color={colors.textTertiary} />}
            title="Couldn't load the registers"
            body="Check your connection and try again."
            cta="Retry"
            onCta={() => { void load(); }}
          />
        ) : (
          <>
            <AppText variant="bodySm" tone="tertiary">
              Generated live from the society&apos;s own records — always
              inspection-ready (Sec. 160, MCS Act).
            </AppText>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: space.s2, marginTop: space.s3 }}>
              <Chip label="Members (I/J)" active={active === 'members'} onPress={() => setActive('members')} />
              <Chip label="Minutes book" active={active === 'minutes'} onPress={() => setActive('minutes')} />
              <Chip label="Cash book" active={active === 'cash'} onPress={() => setActive('cash')} />
            </View>
            {meta ? (
              <View style={{ flexDirection: 'row', gap: 6, marginTop: space.s3, alignItems: 'flex-start' }}>
                <Icon name="lock" size={12} color={colors.accent700} />
                <AppText variant="bodySm" style={{ color: colors.accent700, flex: 1 }}>{meta.citation}</AppText>
              </View>
            ) : null}

            <View style={{ marginTop: space.s3, marginBottom: space.s6 }}>
              {active === 'members' && members ? (
                <Card>
                  {members.map((r, i) => (
                    <View key={r.sr}>
                      {i > 0 ? <Hairline /> : null}
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3, paddingVertical: space.s2 }}>
                        <AppText variant="bodySm" tone="tertiary" mono style={{ width: 26 }}>{r.sr}.</AppText>
                        <View style={{ flex: 1, minWidth: 0 }}>
                          <AppText variant="bodyMd" weight="500">{r.name}</AppText>
                          <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 1 }}>
                            {[r.flat, `admitted ${fmtDay(r.admittedOn)}`].filter(Boolean).join(' · ')}
                          </AppText>
                        </View>
                        <Pill status={r.memberClass === 'PRIMARY' || r.memberClass === 'JOINT' ? 'approved' : 'pending'}
                          label={r.memberClass} />
                      </View>
                    </View>
                  ))}
                </Card>
              ) : null}

              {active === 'minutes' && minutes ? (
                minutes.length === 0 ? (
                  <AppText variant="bodySm" tone="tertiary">No confirmed minutes yet — they appear here the moment a meeting is certified.</AppText>
                ) : (
                  <View style={{ gap: space.s3 }}>
                    {minutes.map((m, i) => (
                      <Card key={i}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s2 }}>
                          <Pill status="pending" label={m.kind} />
                          <View style={{ flex: 1 }} />
                          <AppText variant="bodySm" tone="tertiary">{fmtDay(m.date)}</AppText>
                        </View>
                        <AppText variant="bodyMd" weight="600" style={{ marginTop: space.s2 }}>{m.title}</AppText>
                        <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>
                          {m.venue} · {m.present}/{m.quorumRequired} present — quorum {m.quorumMet ? 'met' : 'NOT met'}
                        </AppText>
                        <AppText variant="bodyMd" style={{ marginTop: space.s2 }}>{m.minutes}</AppText>
                        <AppText variant="bodySm" tone="tertiary" style={{ marginTop: space.s2 }}>
                          Recorded by {m.recordedBy ?? '—'} · confirmed by {m.confirmedBy ?? '—'} (bye-law 136)
                        </AppText>
                      </Card>
                    ))}
                  </View>
                )
              ) : null}

              {active === 'cash' && cash ? (
                <Card>
                  {cash.items.map((r, i) => (
                    <View key={i}>
                      {i > 0 ? <Hairline /> : null}
                      <View style={{ paddingVertical: space.s2 }}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s2 }}>
                          <AppText variant="bodySm" tone="tertiary" style={{ width: 64 }}>
                            {r.date ? new Date(r.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : ''}
                          </AppText>
                          <AppText variant="bodySm" style={{ flex: 1 }} numberOfLines={2}>{r.particulars}</AppText>
                          <AppText variant="bodySm" mono weight="500"
                            style={{ color: r.in > 0 ? colors.primary700 : colors.danger500 }}>
                            {r.in > 0 ? `+${formatINR(r.in / 100)}` : `−${formatINR(r.out / 100)}`}
                          </AppText>
                        </View>
                      </View>
                    </View>
                  ))}
                  <Hairline />
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingTop: space.s3 }}>
                    <AppText variant="bodyMd" weight="600">Closing balance</AppText>
                    <AppText variant="bodyMd" weight="600" mono
                      style={{ color: cash.closingBalance >= 0 ? colors.primary700 : colors.danger500 }}>
                      {cash.closingBalance < 0 ? '−' : ''}{formatINR(Math.abs(cash.closingBalance) / 100)}
                    </AppText>
                  </View>
                  <AppText variant="bodySm" tone="tertiary" style={{ marginTop: space.s2 }}>
                    In-app records only — opening balances from before Bero live in the old books.
                  </AppText>
                </Card>
              ) : null}
            </View>
          </>
        )}
      </Screen>
    </Screen>
  );
}
